import { type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Container } from "@/components/layout/Container";
import { FadeIn } from "@/components/motion/Stagger";
import { cn } from "@/lib/cn";

/**
 * Shown inside a study mode when its asset isn't uploaded yet (signed-URL
 * 404, or an empty deck/quiz). Deliberately reassuring rather than error-ish:
 * the content is coming, and the student has somewhere useful to go next.
 */
export function ContentComingSoon({
  chapterId,
  chapterName,
  title,
  modeIcon: ModeIcon,
  modeAccent,
  className,
}: {
  chapterId: string | undefined;
  chapterName: string | null | undefined;
  /** e.g. "The video for this chapter is almost ready" */
  title: string;
  modeIcon: LucideIcon;
  modeAccent: string;
  className?: string;
}) {
  // chapter_id = "<subject_id>-ch<order>", so the subject is the prefix.
  const subjectId = chapterId?.replace(/-ch\d+$/, "");
  const chaptersHref = subjectId
    ? `/subjects/${encodeURIComponent(subjectId)}/chapters`
    : "/subjects";
  const modesHref = chapterId ? `/study/${encodeURIComponent(chapterId)}` : "/subjects";

  return (
    <Container size="md" className={cn("py-16", className)}>
      <FadeIn>
        <div className="rounded-2xl border border-border bg-surface p-6 sm:p-10 text-center">
          <div className={cn("mx-auto grid place-items-center h-12 w-12 rounded-full", modeAccent)}>
            <ModeIcon className="h-5 w-5" />
          </div>
          <h2 className="mt-5 font-display text-2xl font-semibold tracking-tight">{title}</h2>
          <p className="mt-2 text-text-muted text-sm sm:text-base max-w-md mx-auto">
            Our team is putting the finishing touches on {chapterName || "this chapter"}. New
            chapters go live regularly, so check back soon.
          </p>
          <div className="mt-7 flex items-center justify-center gap-3 flex-wrap">
            <Button asChild>
              <Link to={modesHref}>Try another study mode</Link>
            </Button>
            <Button variant="secondary" asChild>
              <Link to={chaptersHref}>Explore other chapters</Link>
            </Button>
            <Button variant="ghost" asChild>
              <Link to="/contact">Contact us</Link>
            </Button>
          </div>
        </div>
      </FadeIn>
    </Container>
  );
}
